import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, CheckCircle, Tag, Infinity as InfinityIcon, ShieldCheck, Clock } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function PlanTeaser({ plans, pct }) {
  const rows = (plans || []).filter((p) => p.monthly_price != null).slice(0, 3);
  if (!rows.length) return null;
  return (
    <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3" data-testid="partner-plans">
      {rows.map((p) => {
        const after = p.monthly_price * (1 - pct / 100);
        return (
          <div key={p.id} className="rounded-xl border border-white/10 bg-white/[0.04] p-4 backdrop-blur min-w-0" data-testid={`partner-plan-${p.id}`}>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50 truncate">{p.name}</p>
            <p className="mt-2 text-xs text-white/45 line-through">{money(p.monthly_price)} / month</p>
            <p className="text-lg font-bold font-[Chivo] text-white leading-tight">{money(after)}</p>
            <p className="text-[11px] text-white/60">per month with code</p>
            <p className="text-[11px] text-emerald-300 mt-1">{p.trial_days ? `${p.trial_days}-day free trial` : "free trial"}</p>
          </div>
        );
      })}
    </div>
  );
}

/** Public reseller landing page: fixlr.app/partner/PETE10 — shows the discount and drops the visitor into sign-up with the code applied. */
export default function PublicPartnerPage() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState(false);

  useEffect(() => {
    document.title = "Fixlr partner offer";
    axios.get(`${API}/public/partner/${encodeURIComponent(code || "")}`).then(({ data }) => setData(data)).catch(() => setError(true));
    axios.get(`${API}/billing/plans`).then(({ data }) => setPlans(Array.isArray(data) ? data : [])).catch(() => {});
  }, [code]);

  const signup = `/signup?code=${encodeURIComponent((code || "").toUpperCase())}`;
  return (
    <div className="min-h-screen bg-[#191A12] text-white" data-testid="public-partner-page">
      <header className="border-b border-white/10">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <a href="https://fixlr.app" className="flex items-center gap-3" data-testid="partner-home"><img src="/fixlr-logo-light.png" alt="Fixlr" className="h-8 w-auto" /><span className="text-xs font-semibold tracking-[0.2em] uppercase text-white/50 border-l border-white/15 pl-3">Partner offer</span></a>
          <Link to="/login" className="text-xs text-white/60 hover:text-white" data-testid="partner-login-link">Already a customer? Sign in</Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 py-14 lg:py-20">
        {error || (data && !data.valid) ? (
          <div className="max-w-xl" data-testid="partner-invalid">
            <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-amber-300">This link isn't active</p>
            <h1 className="text-4xl sm:text-5xl font-bold font-[Chivo] mt-3 leading-tight">We couldn't find partner code <span className="font-mono text-white/70">{(code || "").toUpperCase()}</span></h1>
            <p className="text-white/60 mt-4 text-base">The code may have been switched off or mistyped. You can still start a free Fixlr trial — or ask your partner for their current code.</p>
            <Link to="/signup" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-[#191A12] px-6 py-3 font-semibold hover:bg-white/90 transition-colors" data-testid="partner-signup-plain">Start a free trial <ArrowRight size={16} weight="bold" /></Link>
          </div>
        ) : !data ? (
          <p className="text-white/50 text-sm">Loading your offer…</p>
        ) : (
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-12 items-start">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-3 py-1 text-xs text-emerald-200" data-testid="partner-code-chip">
                <Tag size={13} weight="fill" /> Code <span className="font-mono font-bold tracking-wider">{data.code}</span> · {data.discount_pct}% off for life
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-[Chivo] mt-5 leading-[1.05]" data-testid="partner-headline">{data.reseller.headline}</h1>
              <p className="text-base md:text-lg text-white/65 mt-5 max-w-xl leading-relaxed" data-testid="partner-intro">{data.reseller.intro}</p>
              <div className="flex items-center gap-3 mt-4" data-testid="partner-byline">
                {data.reseller.photo_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${data.reseller.photo_url}`} alt={data.reseller.display_name} className="h-12 w-12 rounded-full object-cover border border-white/15 bg-white/5" data-testid="partner-photo" />}
                <p className="text-sm text-white/45">Shared by <b className="text-white/80">{data.reseller.display_name}</b>{data.reseller.company ? ` · ${data.reseller.company}` : ""}, an official Fixlr partner.</p>
              </div>
              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link to={signup} className="inline-flex items-center gap-2 rounded-full bg-emerald-400 text-[#191A12] px-7 py-3.5 font-bold hover:bg-emerald-300 transition-colors shadow-[0_0_0_6px_rgba(52,211,153,0.15)]" data-testid="partner-signup-cta">
                  Start free trial with {data.discount_pct}% off <ArrowRight size={18} weight="bold" />
                </Link>
                <span className="text-xs text-white/45">No card needed to start · the code is already applied</span>
              </div>
              <ul className="mt-10 grid sm:grid-cols-3 gap-4 text-sm">
                {[[InfinityIcon, "For life", `${data.discount_pct}% off every monthly or annual payment, as long as you stay subscribed.`],
                  [Clock, "Free trial first", "Try everything before you pay. The discount applies the moment you subscribe."],
                  [ShieldCheck, "Nothing to claim", "No vouchers or chasing — it's baked into your bill automatically."]].map(([Icon, t, d]) => (
                    <li key={t} className="rounded-xl border border-white/10 p-4"><Icon size={20} className="text-emerald-300" /><p className="font-semibold mt-2">{t}</p><p className="text-white/55 text-xs mt-1 leading-relaxed">{d}</p></li>
                  ))}
              </ul>
            </div>
            <aside className="space-y-4 lg:pt-10">
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/40">Your price with the code</p>
              <PlanTeaser plans={plans} pct={data.discount_pct} />
              <ul className="rounded-xl border border-white/10 bg-white/[0.03] p-4 text-sm space-y-2 text-white/70" data-testid="partner-whats-included">
                {["Jobs, quotes, invoices and PPM in one place", "Engineer app with photos, checklists and signatures", "Client & supplier portals, compliance and safety tools", "Payroll, HR and accounts add-ons when you need them"].map((x) => (
                  <li key={x} className="flex items-start gap-2"><CheckCircle size={16} weight="fill" className="text-emerald-300 mt-0.5 shrink-0" /> {x}</li>
                ))}
              </ul>
              <p className="text-[11px] text-white/40">Enter <span className="font-mono text-white/70">{data.code}</span> on the sign-up page if it isn't pre-filled. Discounts can't be added once a subscription is live.</p>
            </aside>
          </div>
        )}
      </main>
      <footer className="border-t border-white/10 mt-10">
        <div className="max-w-5xl mx-auto px-5 py-6 flex flex-wrap items-center justify-between gap-2 text-xs text-white/40">
          <span>© {new Date().getFullYear()} Fixlr</span>
          <span className="flex gap-4"><a href="https://fixlr.app" className="hover:text-white">fixlr.app</a><Link to="/help" className="hover:text-white">Help Centre</Link></span>
        </div>
      </footer>
    </div>
  );
}
